// CentraJob Color Palette - Clean, Minimal UI Theme
// Simple black, white, and light blue accent

export const colors = {
  // Primary color - Light Blue accent for highlights
  primary: {
    main: '#513cf1',
    light: '#7b6ae8',
    dark: '#4531ca',
    contrastText: '#ffffff',
  },

  // Secondary color - Neutral grey (simplified)
  secondary: {
    main: '#6B7280',
    light: '#9CA3AF',
    dark: '#4B5563',
    contrastText: '#FFFFFF',
  },

  // Error color (Red) - Keep for status
  error: {
    main: '#DC2626',
    light: '#FEE2E2',
    dark: '#B91C1C',
    contrastText: '#FFFFFF',
  },

  // Warning color (Amber) - Keep for status
  warning: {
    main: '#D97706',
    light: '#FEF3C7',
    dark: '#B45309',
    contrastText: '#FFFFFF',
  },

  // Success color (Green) - Keep for status
  success: {
    main: '#059669',
    light: '#D1FAE5',
    dark: '#047857',
    contrastText: '#FFFFFF',
  },

  // Info color - Use primary blue
  info: {
    main: '#3B82F6',
    light: '#DBEAFE',
    dark: '#2563EB',
    contrastText: '#FFFFFF',
  },

  // Grey palette - Neutral tones
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    300: '#D4D4D4',
    400: '#A3A3A3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },

  // Background colors
  background: {
    default: '#FAFAFA',
    paper: '#FFFFFF',
    toolbar: '#FFFFFF',
    surface: '#FAFAFA',
    light: '#F5F5F5',
  },

  // Text colors - Black/Grey hierarchy
  text: {
    primary: '#171717',
    secondary: '#525252',
    disabled: '#A3A3A3',
    hint: '#737373',
  },

  // Common colors
  common: {
    white: '#FFFFFF',
    black: '#000000',
    transparent: 'transparent',
  },

  // Divider
  divider: '#E5E5E5',
} as const;

// Brand color shades (Blue - simplified)
export const brand = {
  shade1: '#F0F9FF',
  shade2: '#E0F2FE',
  shade3: '#DBEAFE',
  shade4: '#BFDBFE',
  shade5: '#93C5FD',
  shade6: '#60A5FA',
  shade7: '#3B82F6',
  shade8: '#2563EB',  // Primary
  shade9: '#1D4ED8',
  shade10: '#1E40AF',
  shade11: '#1E3A8A',
  shade12: '#172554',
} as const;

// Error color shades (Red)
export const error = {
  shade1: '#FFFBFA',
  shade2: '#FEF3F2',
  shade3: '#FEE4E2',
  shade4: '#FECDCA',
  shade5: '#FDA29B',
  shade6: '#F97066',
  shade7: '#F04438',  // Primary
  shade8: '#D92D20',
  shade9: '#B42318',
  shade10: '#912018',
  shade11: '#7A271A',
  shade12: '#55160C',
} as const;

// Warning color shades (Orange)
export const warning = {
  shade1: '#FFFCF5',
  shade2: '#FEF3F2',
  shade3: '#FEF0C7',
  shade4: '#FEDF89',
  shade5: '#FEC84B',
  shade6: '#FDB022',
  shade7: '#F79009',  // Primary
  shade8: '#DC6803',
  shade9: '#B54708',
  shade10: '#93370D',
  shade11: '#7A2E0E',
  shade12: '#4E1D09',
} as const;

// Success color shades (Green)
export const success = {
  shade1: '#F6FEF9',
  shade2: '#ECFDF3',
  shade3: '#DCFAE6',
  shade4: '#ABEFC6',
  shade5: '#75E0A7',
  shade6: '#47CD89',  // Primary
  shade7: '#17B26A',
  shade8: '#079455',
  shade9: '#067647',
  shade10: '#085D3A',
  shade11: '#074D31',
  shade12: '#053321',
} as const;

// Grey Light Mode shades
export const greyLightMode = {
  shade1: '#FDFDFD',
  shade2: '#FAFAFA',
  shade3: '#F5F5F5',  // Primary
  shade4: '#E9EAEB',
  shade5: '#D5D7DA',
  shade6: '#A4A7AE',
  shade7: '#717680',
  shade8: '#535862',
  shade9: '#414651',
  shade10: '#252B37',
  shade11: '#181D27',
  shade12: '#0A0D12',
  shade13: '#D9D9D9',
  shade14: '#D1D0CE',
} as const;


// White color shades
export const white = {
  primary: '#FFFFFF',
  shade1: '#F7F6F3',
  shade2: '#E4E8ED',
  shade3: '#F8F6EF',
  shade4: '#F9F9F9',
  shade5: '#F8F9FC',
  shade6: '#E4E7EC',
} as const;

// Black color shades
export const black = {
  primary: '#000000',
  shade1: '#161616',
  shade2: '#101828',
} as const;

// Blue color
export const blue = {
  primary: '#5031E8',
  shade1: '#D5D9EB',
  shade13: '#363F72',
} as const;

// Shadow color
export const shadowColor = 'rgba(10, 13, 18, 0.05)';

// CJ Custom colors for convenience - Simplified
export const cj = {
  // Background variants - Clean whites/greys
  backgroundPrimary: '#FFFFFF',
  backgroundSecondary: '#FAFAFA',
  backgroundTertiary: '#F5F5F5',

  // Text variants - Black/grey hierarchy
  textPrimary: '#171717',
  textSecondary: '#525252',
  textTertiary: '#737373',
  textDisabled: '#A3A3A3',

  // Border - Subtle grey
  border: '#E5E5E5',
  divider: '#D4D4D4',

  // Brand/Primary - Light blue accent
  brandPrimary: '#3B82F6',
  brandLight: '#DBEAFE',

  // Status backgrounds - Muted
  errorBackground: '#FEE2E2',
  warningBackground: '#FEF3C7',
  successBackground: '#D1FAE5',
  infoBackground: '#DBEAFE',
} as const;
